import {
  object,
  string,
  union,
  literal,
  boolean,
  preprocess,
  ZodIssueCode,
} from 'zod';

const pfmSchema = object({
  PFM_API_URL: string().url().describe('Base URL for API requests'),
  PFM_DESKTOP_CONSUMER_NAME: string()
    .default('aviata-kz.web.desktop')
    .describe('Desktop consumer name'),
  PFM_DESKTOP_CONSUMER_KEY: string().describe(
    'Desktop consumer key to generate consumer token',
  ),
  PFM_MOBILE_CONSUMER_NAME: string()
    .default('aviata-kz.web.mobile')
    .describe('Mobile consumer name'),
  PFM_MOBILE_CONSUMER_KEY: string().describe(
    'Mobile consumer key to generate consumer token',
  ),
  PFM_IOS_CONSUMER_NAME: string()
    .default('aviata-kz.app.ios')
    .describe('iOS consumer name'),
  PFM_IOS_CONSUMER_KEY: string().describe(
    'iOS consumer key to generate consumer token',
  ),
  PFM_ANDROID_CONSUMER_NAME: string()
    .default('aviata-kz.app.android')
    .describe('Android consumer name'),
  PFM_ANDROID_CONSUMER_KEY: string().describe(
    'Android consumer key to generate consumer token',
  ),
});

const sentrySchema = object({
  SENTRY_DSN: string()
    .url()
    .optional()
    .describe('Sentry DSN for error tracking'),
});

/**
 * Firebase configs
 * @see https://wiki.yandex.ru/tours/frontendv2/remote-config/#okruzheniya
 */
const firebaseSchema = object({
  FIREBASE_API_KEY: string().describe('Firebase API key').optional(),
  FIREBASE_PROJECT_ID: string().describe('Firebase project id').optional(),
  FIREBASE_APP_ID: string().describe('Firebase app id').optional(),
});

const aviataLinksSchema = object({
  AVIATA_URL: string()
    .url()
    .default('https://aviax-staging-frontend.dev.cloud.aviata.team')
    .describe('Link to Aviata air main page'),
  AVIATA_RW_URL: string()
    .url()
    .default('https://aviax-staging-frontend.dev.cloud.aviata.team/railways')
    .describe('Link to Aviata railways main page'),
  AVIATA_HOTELS_URL: string()
    .url()
    .default('https://hotels.aviata.kz/')
    .describe('Link to Aviata hotels page'),
  AVIATA_BLOG_URL: string()
    .url()
    .default('https://blog-staging-frontend.dev.cloud.aviata.team/media')
    .describe('Link to Aviata blog page'),
  AVIATA_CABINET_URL: string()
    .url()
    .default('https://aviata-cabinet-staging-platform.dev.cloud.aviata.team')
    .describe('Link to Aviata cabinet page'),
  AVIATA_CABINET_PASSENGERS_URL: string()
    .url()
    .default(
      'https://aviata-cabinet-staging-platform.dev.cloud.aviata.team/#/passengers',
    )
    .describe('Link to Aviata cabinet tours page'),

  AVIATA_ABOUT_URL: string()
    .url()
    .default('https://aviax-staging-frontend.dev.cloud.aviata.team/about')
    .describe('Link to the about Aviata page'),
  AVIATA_AGREEMENT_URL: string()
    .url()
    .default('https://aviax-staging-frontend.dev.cloud.aviata.team/agreement')
    .describe('Link to Aviata public offer page'),
  AVIATA_JOBS_URL: string()
    .url()
    .default('https://special.aviata.kz/job')
    .describe('Link to Aviata jobs page'),
  AVIATA_CONTACTS_URL: string()
    .url()
    .default('https://aviax-staging-frontend.dev.cloud.aviata.team/contacts')
    .describe('Link to Aviata contacts page'),
  AVIATA_PRIVACY_URL: string()
    .url()
    .default(
      'https://aviax-staging-frontend.dev.cloud.aviata.team/privacy_policy',
    )
    .describe('Link to Aviata privacy policy page'),
  AVIATA_B2B_URL: string()
    .url()
    .default('https://aviax-staging-frontend.dev.cloud.aviata.team/b2b_landing')
    .describe('Link to Aviata b2b landing page'),
  AVIATA_LOYALTY_URL: string()
    .url()
    .default('https://aviax-staging-frontend.dev.cloud.aviata.team/bonuses')
    .describe('Link to Aviata loyalty landing page'),
  KAZTOUR_WEBVIEW_URL: string()
    .url()
    .default('https://dev-aviata.kaztour.kz')
    .describe('Link to Kaztour webview'),
  SUPERAPP_URL: string()
    .url()
    .default('https://freedom-mini-apps-staging-frontend.dev.cloud.aviata.team')
    .describe('Link to Freedom SuperApp webview'),
});

const whatsAppSchema = object({
  WHATSAPP_URL: string()
    .url()
    .default('https://wa.me/+77711408163')
    .describe('Aviata tours WhatsApp URL'),
  IS_CHAT_VISIBLE: preprocess(val => val === 'true', boolean())
    .default(false)
    .describe('Whether the WhatsApp icon is visible'),
});

const schema = object({
  MODE: union([
    literal('development'),
    literal('staging'),
    literal('production'),
  ])
    .default('development')
    .describe('Environment mode of the build/dev'),
  IS_SSR: boolean().describe('Whether the app is running on the server'),

  BASE_URL: string()
    .url()
    .default('http://localhost:8000')
    .describe('Domain the app is served from'),
  BASE_SERVER_PREFIX: string()
    .default('/')
    .describe('Path the app is served from'),
  STATIC_BASE_URL: string()
    .url()
    .default('http://localhost:8000/static')
    .describe('URL static assets are loaded from'),

  GOOGLE_ANALYTICS_ID: string()
    .optional()
    .describe('Google analytics id to send product events'),
  YA_METRIKA_ID: string()
    .optional()
    .describe('Yandex metrika counter id for dev metrics'),

  PIPELINE_IID: string()
    .optional()
    .describe('To display the pipeline id in header for stage builds'),

  CF_TURNSTILE_SITEKEY: string().describe('Site key for cloudflare turnstile'),

  ...pfmSchema.shape,
  ...sentrySchema.shape,
  ...firebaseSchema.shape,
  ...aviataLinksSchema.shape,
  ...whatsAppSchema.shape,
}).superRefine((schema, ctx) => {
  /**
   * @description Require SENTRY_DSN in production mode
   */
  if (schema.MODE === 'production' && schema.SENTRY_DSN === undefined) {
    ctx.addIssue({
      code: ZodIssueCode.custom,
      message: 'SENTRY_DSN is required in production mode',
      path: ['SENTRY_DSN'],
      params: { MODE: schema.MODE },
    });
  }

  /**
   * @description Require GOOGLE_ANALYTICS_ID in production mode
   */
  if (
    schema.MODE === 'production' &&
    schema.GOOGLE_ANALYTICS_ID === undefined
  ) {
    ctx.addIssue({
      code: ZodIssueCode.custom,
      message: 'GOOGLE_ANALYTICS_ID is required in production mode',
      path: ['GOOGLE_ANALYTICS_ID'],
      params: { MODE: schema.MODE },
    });
  }

  /**
   * @description Require YA_METRIKA_ID in production mode
   */
  if (schema.MODE === 'production' && schema.YA_METRIKA_ID === undefined) {
    ctx.addIssue({
      code: ZodIssueCode.custom,
      message: 'YA_METRIKA_ID is required in production mode',
      path: ['YA_METRIKA_ID'],
      params: { MODE: schema.MODE },
    });
  }
});

export const envConfig = {
  schema,
};
